import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import {
  Component,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ElementRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, of, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { ContactPersonModel } from '@models/contact-person.model';
import { MediaVideoModel } from '@modules/media/models/media-video.model';
import { MediaType } from '@modules/media/models/media.types';
import { PlayerModel } from '@modules/media/models/player.model';
import { TranscriptEntryModel, TRANSCRIPT_ENTRY_TYPE } from '@modules/media/models/transcript-entry.model';
import { TranscriptService } from '@modules/media/service/transcript.service';
import { PlayerType, VideoComponent } from '@modules/media/video/video.component';
import { ToastService } from '@modules/toast/toast.service';
import {
  ActionIcons,
  ButtonItem,
  ActionContainerComponent,
} from '@modules/ui/action-container/action-container.component';

import { DocumentCardComponent } from '../../../card/document-card/document-card.component';
import { AudioComponent } from '../../../media/audio/audio.component';
import { ImageComponent } from '../../../media/image/image.component';
import { BufferingComponent } from '../../../ui/buffering/buffering.component';

@Component({
  selector: 'mb-transcript-content',
  templateUrl: './transcript-content.component.html',
  styleUrls: ['./transcript-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgTemplateOutlet,
    ImageComponent,
    ActionContainerComponent,
    VideoComponent,
    DocumentCardComponent,
    AudioComponent,
    BufferingComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class TranscriptContentComponent implements OnInit, OnDestroy {
  private toastService = inject(ToastService);
  private transcriptService = inject(TranscriptService);

  private destroy$ = new Subject<boolean>();
  private _isInAutoScroll = false;

  private set isInAutoScroll(v: boolean) {
    this._isInAutoScroll = v;
    if (this.lastTimeout) {
      clearTimeout(this.lastTimeout);
    }
    // disable autoscroll mode after 50ms
    if (v === true) {
      this.lastTimeout = setTimeout(() => {
        this.isInAutoScroll = false;
      }, 50);
    }
  }

  private get isInAutoScroll() {
    return this._isInAutoScroll;
  }

  lastActiveId: string | null = null;
  lastTimeout: any = null;

  public buttonItems: ButtonItem[] = [{ icon: ActionIcons.BOOKMARK }];
  public entryTypes = TRANSCRIPT_ENTRY_TYPE;
  public mediaTypes = MediaType;
  public playerType = PlayerType;

  public defaultAction = [ActionIcons.SHARE, ActionIcons.DOWNLOAD, ActionIcons.BOOKMARK];

  @ViewChild('transcriptContentInner', { static: false })
  transcriptContentInner!: ElementRef;

  @Input() disableActionAssets!: boolean;
  @Input() disabledActionAssetsToastText!: string;
  @Input() currentEntry: TranscriptEntryModel | null = null;
  @Input() speakers!: ContactPersonModel[];
  @Input() entries$: Observable<TranscriptEntryModel[]> = of([]);
  @Input() displaySpeaker = true;
  @Input() player!: PlayerModel;
  @Input() isMobile = false;

  @Input() activeEntry = new Subject<TranscriptEntryModel>();

  private _synced = true;
  @Input()
  set synced(v: boolean) {
    this._synced = v;
    if (this._synced) {
      this.activeEntry.pipe(take(1)).subscribe((entry) => this.scrollToComment(entry));
    }
  }
  get synced() {
    return this._synced;
  }

  @Output() scrolled = new EventEmitter();
  @Output() openMedia = new EventEmitter();

  public isLoading() {
    return this.transcriptService.isLoadingEntries;
  }

  private scrollToComment(entry: TranscriptEntryModel) {
    if (!this.transcriptContentInner) {
      return;
    }
    const innerContent = this.transcriptContentInner.nativeElement;
    const childrenList = innerContent.querySelectorAll(`[data-id="${entry.id}"]`);
    const commentary = childrenList.length > 0 ? childrenList[0] : null;
    if (commentary) {
      this.isInAutoScroll = true;
      const extraOffset = innerContent.children[0]?.scrollHeight;
      innerContent.scrollTop = commentary.offsetTop - (extraOffset || 0) - 60; // 60 is the intro height
    }
  }

  public checkCanOpen() {
    if (this.disableActionAssets) {
      this.toastService.open({
        text: this.disabledActionAssetsToastText || 'DISABLED_ACTION_DEFAULT_TEXT',
      });
      return false;
    }
    return true;
  }

  public contentScrolled() {
    if (!this.isInAutoScroll) {
      this.scrolled.next(true);
    }
  }

  public openVideoDialog(video: MediaVideoModel) {
    if (!this.checkCanOpen()) {
      return;
    }
    this.openMedia.emit(video);
  }

  public emitOpenMedia(resource: any) {
    this.openMedia.emit(resource);
  }

  ngOnInit(): void {
    this.activeEntry.pipe(takeUntil(this.destroy$)).subscribe((d) => {
      // remember last active id to not scroll unnecessary
      if (this.lastActiveId !== d.id) {
        // wait for angular to render, so the id can be found
        setTimeout(() => {
          this.scrollToComment(d);
        });
      }
      this.lastActiveId = d.id;
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
